import { type ReactNode } from "react";

import { SubscriptionTier } from "@repo/ui/pricing-hooks";
import { Button } from "@shapr3d/frontend-lib";

import { useHasStudioPricing } from "../../../hooks/usePriceConfig";
import { useTracker } from "../../../hooks/useTracker";
import useTranslation from "../../../hooks/useTranslation";
import { Modal } from "../../../lib/modal";
import { ElementClickAction, ElementClickLocation } from "../../../lib/TrackingAdapter";
import { getTierDisplayName } from "../subscriptionUtils";

import style from "./SubscriptionCancelModal.module.css";

const CancelMessage: React.FC<{
  editorSeatCount: number;
  isSubscriptionInGracePeriod: boolean;
  subscriptionExpirationDate: string;
  subscriptionTier: SubscriptionTier;
}> = ({
  editorSeatCount,
  subscriptionExpirationDate,
  isSubscriptionInGracePeriod,
  subscriptionTier,
}) => {
  const { isStudioTier } = useHasStudioPricing();
  const freeTierName = isStudioTier ? "Free" : "Basic";

  const { t, m } = useTranslation();

  if (editorSeatCount > 1) {
    if (isSubscriptionInGracePeriod) {
      return (
        <p className="expirationContainer">
          {t(m.TeamBilling.CancelMessageTeamGracePeriod, {
            freeTierName,
          })}
        </p>
      );
    }

    return (
      <p className="expirationContainer">
        {t(m.TeamBilling.CancelMessageTeam, {
          freeTierName,
          date: subscriptionExpirationDate,
          styledDate: (chunks: ReactNode) => (
            <span key="date" className={style.expirationDate}>
              {chunks}
            </span>
          ),
        })}
      </p>
    );
  }

  return (
    <p className="expirationContainer">
      {t(m.TeamBilling.CancelMessageSolo, {
        freeTierName,
        tierName: getTierDisplayName(subscriptionTier),
        date: subscriptionExpirationDate,
        styledDate: (chunks: ReactNode) => (
          <span key="date" className={style.expirationDate}>
            {chunks}
          </span>
        ),
      })}
    </p>
  );
};

type SubscriptionCancelModalProps = {
  open: boolean;
  isLoading: boolean;
  editorSeatCount: number;
  isSubscriptionInGracePeriod: boolean;
  subscriptionExpirationDate: string;
  subscriptionTier: SubscriptionTier;
  onClose: () => void;
  onCTA: () => void;
};

export const SubscriptionCancelModal: React.FC<SubscriptionCancelModalProps> = ({
  open,
  isLoading,
  editorSeatCount,
  isSubscriptionInGracePeriod,
  subscriptionExpirationDate,
  subscriptionTier,
  onClose,
  onCTA,
}) => {
  const { t, m } = useTranslation();
  const tracker = useTracker();

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t(m.TeamBilling.CancelSubscription)}
      size="medium"
      telemetryName="cancel_subscription"
    >
      <div className={style.modalContainer}>
        <div>
          <CancelMessage
            editorSeatCount={editorSeatCount}
            isSubscriptionInGracePeriod={isSubscriptionInGracePeriod}
            subscriptionExpirationDate={subscriptionExpirationDate}
            subscriptionTier={subscriptionTier}
          />
          <p>{t(m.TeamBilling.AreYouSureCancel)}</p>
        </div>

        <div className={style.modalButtonContainer}>
          <Button
            onClick={() => {
              tracker.trackButtonClick({
                action: ElementClickAction.CANCEL,
                location: ElementClickLocation.CANCEL_SUBSCRIPTION_MODAL,
              });
              onClose();
            }}
            disabled={isLoading}
            variant="grayscale"
          >
            {t(m.TeamBilling.KeepTier, {
              tierName: getTierDisplayName(subscriptionTier),
            })}
          </Button>
          <Button
            onClick={() => {
              onCTA();
              tracker.trackButtonClick({
                action: ElementClickAction.CONTINUE,
                location: ElementClickLocation.CANCEL_SUBSCRIPTION_MODAL,
              });
            }}
            disabled={isLoading}
            variant="red"
          >
            {t(m.TeamBilling.ContinueCancellation)}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
